<vs-dialog
  *ngIf="item$ | async as item"
  [config]="{ dialogContainerClass: 'w-[576px] max-w-[800px]', startOpen: true }"
  (closed)="navigateTo(!item.adjudicationReason ? '../cure' : undefined)"
>
  <vs-dialog-header
    [config]="{
      mode: 'simple',
      showDivider: true,

      icon: 'file-plus-03',
      iconTheme: 'primary',

      title: (item?.status !== reviewing ? 'Edit ' + (item?.coverage?.title ?? 'Other') : 'Add ' + (item?.coverage?.title ?? '')) + ' Adjudication',
      subtitle: 'Review the details of this claim item to adjudicate it.'

    }"
  ></vs-dialog-header>

  <form *ngIf="item" class="flex flex-col gap-4 p-4" [formGroup]="form">
    <ng-container *ngIf="claim$ | async as claim">
      <div *ngIf="charges?.length" class="border rounded-xl px-4 py-3" data-testid="decision-summary">
        <div class="flex items-center gap-2 mb-2">
          <p class="text-sm font-semibold text-gray-900 truncate">{{ item.coverage?.title ?? config.otherCoverage }}</p>
          <vs-badge *ngIf="item.coverage?.title" [theme]="isIncluded(claim, item) ? 'success' : 'default'">
            {{ isIncluded(claim, item) ? config.form.included : config.form.excluded }}
          </vs-badge>
        </div>

        <dl class="flex flex-col gap-1 text-sm">
          <div class="flex justify-between">
            <dt class="text-gray-500">{{ config.labels.requested }}</dt>
            <dd data-testid="summary-requested">{{ formatAmount(requestedTotal()) }}</dd>
          </div>
          <div class="flex justify-between">
            <dt class="text-gray-500">{{ config.labels.authorized }}</dt>
            <dd data-testid="summary-authorized">{{ formatAmount(currentAuthorizedTotal()) }}</dd>
          </div>
          <div class="flex justify-between items-center">
            <dt class="text-gray-500">{{ config.labels.status }}</dt>
            <dd>
              <vs-badge [theme]="statusTheme(item.status)">
                {{ item.status === reviewing ? config.form.reviewing : (item.status | titlecase) }}
              </vs-badge>
            </dd>
          </div>
          <div *ngIf="item.adjudicationReason" class="flex justify-between gap-4">
            <dt class="text-gray-500">{{ config.labels.reason }}</dt>
            <dd class="text-right text-gray-700">{{ item.adjudicationReason }}</dd>
          </div>
        </dl>

        <ws-claim-item-liability-limit
          *ngIf="item.liabilityInfo"
          class="block mt-2"
          [item]="{ coverage: item.coverage, liabilityInfo: item.liabilityInfo }"
          [isLiabilityPerClaim]="claim.warranty?.plan?.liabilityPerClaim ?? false"
        ></ws-claim-item-liability-limit>

        <p
          *ngIf="!checksEligibility && usesNewFlow(claim)"
          class="text-sm text-gray-500 mt-2"
          data-testid="summary-eligibility"
        >
          {{ config.noEligibilityLimit }}
        </p>
      </div>

      <ws-claim-item-eligibility
        *ngIf="checksEligibility"
        [compact]="true"
        [claim]="claim"
        [item]="item"
        [canCheck]="!resolved"
        [authorizedAmount]="form.value.status === approved ? (charges?.length ? authorizedTotal() : null) : null"
        (latestChange)="eligibility = $event"
      ></ws-claim-item-eligibility>

      <vs-input-container [vsMenuTriggerFor]="complaint">
        <p slot="start" class="font-medium text-gray-500">Complaint</p>
        <vs-icon slot="end" name="chevron-down"></vs-icon>
      </vs-input-container>

      <ng-template #complaint>
        <vs-menu class="w-[486px]">
          <div class="flex gap-2 px-6 py-2">
            <div class="basis-1/4">
              <p class="text-sm font-medium text-gray-700 whitespace-nowrap">Customer's Complaint</p>
            </div>

            <div class="basis-3/4">
              <p class="text-sm font-normal text-gray-900">{{ claim?.complaint }}</p>
            </div>
          </div>
        </vs-menu>
      </ng-template>

      <vs-input-container [vsMenuTriggerFor]="details">
        <p slot="start" class="font-medium text-gray-500">Item Details</p>
        <vs-icon slot="end" name="chevron-down"></vs-icon>
      </vs-input-container>

      <ng-template #details>
        <vs-menu class="w-[486px]">
          <ws-claim-item-details-card [claim]="claim" [item]="item"></ws-claim-item-details-card>
        </vs-menu>
      </ng-template>

      <vs-input-container [vsMenuTriggerFor]="cure">
        <p slot="start" class="font-medium text-gray-500">Cure Details</p>
        <vs-icon slot="end" name="chevron-down"></vs-icon>
      </vs-input-container>

      <ng-template #cure>
        <vs-menu class="w-[486px]">
          <ws-claim-item-cure-card [item]="item"></ws-claim-item-cure-card>
        </vs-menu>
      </ng-template>
    </ng-container>

    <vs-divider></vs-divider>

    <p class="font-medium text-gray-500">Adjudicate Claim Item</p>

    <vs-input-group theme="primary">
      <button
        vs-button
        class="grow"
        appearance="outline"
        [class.bg-themed-50]="form.value.status === reviewing"
        (click)="changeStatus(reviewing)"
      >
        <input vsInput type="radio" class="cursor-pointer" formControlName="status" [value]="reviewing" />
        Reviewing
      </button>

      <button
        vs-button
        class="grow"
        appearance="outline"
        [class.bg-themed-50]="form.value.status === approved"
        (click)="changeStatus(approved)"
      >
        <input vsInput type="radio" class="cursor-pointer" formControlName="status" [value]="approved" />
        Approved
      </button>

      <button
        vs-button
        class="grow"
        appearance="outline"
        [class.bg-themed-50]="form.value.status === rejected"
        (click)="changeStatus(rejected)"
      >
        <input vsInput type="radio" class="cursor-pointer" formControlName="status" [value]="rejected" />
        Rejected
      </button>
    </vs-input-group>

    <p
      *ngIf="openHolds && form.value.status === approved && !resolved"
      class="flex items-center gap-2 text-sm text-danger-600"
      data-testid="decision-open-holds"
    >
      <vs-icon name="alert-triangle"></vs-icon> {{ controlsConfig.adjudicate.openHolds }}
    </p>

    <p *ngIf="!charges && !loadFailed" class="text-sm text-gray-500">{{ config.form.loading }}</p>
    <p *ngIf="loadFailed" class="text-sm text-danger-600" data-testid="decision-load-failed">
      {{ config.form.loadFailed }}
    </p>

    <vs-form-field *ngIf="charges && !charges.length">
      <div class="flex flex-col gap-1">
        <span class="text-sm font-medium text-gray-700 self-start">Reasoning</span>
        <textarea
          vsInput
          min="2"
          max="5"
          type="text"
          formControlName="reason"
          placeholder="Please enter the reason why you are approving or rejecting this claim item."
          [disabled]="form.value.status === reviewing"
        ></textarea>
      </div>
    </vs-form-field>

    <p *ngIf="charges?.length && resolved" class="text-sm text-gray-600" data-testid="decision-resolved">
      {{ config.form.resolved }}
    </p>

    <div
      *ngIf="charges?.length && !resolved"
      class="flex flex-col gap-4"
      [formGroup]="decisionForm"
      data-testid="decision-form"
    >
      <ng-container *ngIf="form.value.status === approved">
        <div class="border rounded-xl overflow-x-auto">
          <table class="w-full text-sm" data-testid="decision-charges">
            <thead>
              <tr class="text-left text-xs text-gray-500 border-b">
                <th class="pl-4 pr-2 py-2 font-medium">{{ config.form.columns.type }}</th>
                <th class="px-2 py-2 font-medium">{{ config.form.columns.description }}</th>
                <th class="px-2 py-2 font-medium text-right whitespace-nowrap">{{ config.form.columns.requested }}</th>
                <th class="px-2 py-2 font-medium text-right whitespace-nowrap">
                  {{ controlsConfig.charges.allowedColumn }}
                </th>
                <th class="pl-2 pr-4 py-2 font-medium text-right whitespace-nowrap">
                  {{ config.form.columns.authorized }}
                </th>
              </tr>
            </thead>

            <tbody formArrayName="amounts">
              <tr *ngFor="let line of charges; let i = index" class="border-b last:border-b-0">
                <td class="pl-4 pr-2 py-2">
                  <vs-badge theme="default">{{ line.category | titlecase }}</vs-badge>
                </td>
                <td class="px-2 py-2 break-words">{{ line.description }}</td>
                <td class="px-2 py-2 text-right whitespace-nowrap">{{ formatAmount(line.requestedAmount) }}</td>
                <td class="px-2 py-2 text-right whitespace-nowrap" data-testid="decision-allowed">
                  {{ formatAmount(line.allowedAmount) }}
                </td>
                <td class="pl-2 pr-4 py-2 w-32 min-w-[8rem]">
                  <!-- Enter would otherwise click the first status button and flip the status. -->
                  <input
                    vsInput
                    type="number"
                    min="0"
                    step="0.01"
                    [formControlName]="i"
                    (keydown.enter)="$event.preventDefault()"
                  />
                  <span
                    *ngIf="aboveAllowed(line, i)"
                    class="block text-xs text-warn-700"
                    data-testid="decision-above-allowed"
                  >
                    {{ controlsConfig.adjudicate.aboveAllowed }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <dl class="flex justify-between text-sm" data-testid="decision-totals">
          <div>
            <dt class="text-gray-500">{{ config.labels.requested }}</dt>
            <dd class="font-medium text-gray-900">{{ formatAmount(requestedTotal()) }}</dd>
          </div>
          <div>
            <dt class="text-gray-500">{{ config.labels.authorized }}</dt>
            <dd class="font-medium text-gray-900">{{ formatAmount(authorizedTotal()) }}</dd>
          </div>
          <div>
            <dt class="text-gray-500">{{ config.labels.difference }}</dt>
            <dd class="font-medium text-gray-900">{{ formatAmount(requestedTotal() - authorizedTotal()) }}</dd>
          </div>
        </dl>

        <p *ngIf="amounts.invalid" class="text-sm text-danger-600" data-testid="decision-amount-hint">
          {{ config.form.amountHint }}
        </p>

        <vs-form-field [config]="{ label: config.labels.reason }">
          <vs-select vsInput formControlName="approveReason" [config]="{ placeholder: config.form.reasonPlaceholder }">
            <li *ngFor="let reason of reasons.approve" vs-select-option [value]="reason.code">{{ reason.label }}</li>
          </vs-select>
        </vs-form-field>
      </ng-container>

      <ng-container *ngIf="form.value.status === rejected">
        <vs-form-field [config]="{ label: config.labels.reason }">
          <vs-select vsInput formControlName="rejectReason" [config]="{ placeholder: config.form.reasonPlaceholder }">
            <li *ngFor="let reason of reasons.reject" vs-select-option [value]="reason.code">{{ reason.label }}</li>
          </vs-select>
        </vs-form-field>

        <p class="text-sm text-gray-500" data-testid="decision-reject-note">{{ config.form.rejectNote }}</p>
      </ng-container>

      <vs-form-field [config]="{ label: form.value.status === reviewing ? config.labels.note : config.labels.details }">
        <textarea
          vsInput
          type="text"
          formControlName="detail"
          [placeholder]="form.value.status === reviewing ? config.form.notePlaceholder : config.form.detailsPlaceholder"
        ></textarea>
      </vs-form-field>
    </div>
  </form>

  <div class="flex gap-2 justify-end">
    <p *ngIf="charges?.length" class="text-xs text-gray-500 self-center mr-auto" data-testid="decision-claim-note">
      {{ config.notClaimAuthorization }}
    </p>
    <button vs-button appearance="outline" (click)="navigateTo(!item.adjudicationReason ? '../cure' : undefined)">
      Cancel
    </button>
    <!-- Wrapped: a disabled button gets no hover, so the tooltip sits on the wrapper -->
    <div tooltipStyle="light" [vsTooltip]="saveBlockedReason() ?? ''">
      <button vs-button theme="primary" [disabled]="saveDisabled()" (click)="save(item)">Save</button>
    </div>
  </div>
</vs-dialog>
